import type { Coluna, Planilha } from '../../../../src/lib/planilhaExcel';
import { dataParaSerialExcel, linhaDeTotais, montarPlanilha } from '../../../../src/lib/planilhaExcel.ts';
import { valorDevido } from '../../../../src/lib/statusBoleto.ts';
import type { ClienteConsolidado, FaturaInadimplente, FiltrosGlobais, UcInadimplente } from '../types';
import { calcularInsightsCarteira, type InsightCarteira } from './insightsCarteira.ts';
import { TIPOS_AVISO, avisoEnviado, recebeuTodosOsAvisos } from './reguaAvisos.ts';

/**
 * Perfis de exportação da Central de Inadimplência — um por aba.
 *
 * Antes existia um único botão "Exportar Excel" no cabeçalho que despejava
 * SEMPRE a carteira inteira, com as mesmas colunas, estivesse o operador na
 * Rota de Cobrança ou nos Maiores Devedores. Pedir "os 30 maiores devedores de
 * hoje" virava trabalho manual no Excel depois de baixar tudo.
 *
 * Aqui cada aba declara o que ela lista (`itens`, já na ordem da tela) e como
 * isso vira planilha (`planilhas`). O modal de exportação usa `itens` para
 * deixar escolher quem entra — todos, os N primeiros ou a dedo — e devolve a
 * lista escolhida para `planilhas`.
 *
 * Tudo aqui é função pura: roda sob `node --test`, sem DOM e sem Supabase.
 */

export interface ContextoExportacao {
  /** Clientes já filtrados pelos filtros globais, ordenados por valor. */
  clientes: ClienteConsolidado[];
  /** Unidades consumidoras inadimplentes (recorte da Rota de Cobrança). */
  ucs: UcInadimplente[];
  /** Avisos da régua automática por id de fatura. */
  avisosPorFatura: Map<string, Set<string>>;
  filtros: FiltrosGlobais;
  totalAberto: number;
  /** "Hoje" da exportação — parâmetro para o cálculo de atraso ser testável. */
  hoje: Date;
}

/**
 * O recorte que a aba está mostrando AGORA, com os filtros de dentro dela.
 *
 * Os filtros globais do topo já chegam à exportação pelo `ContextoExportacao`,
 * mas cada aba tem os seus próprios (faixa de atraso, rateio encerrado, busca,
 * cliente aberto no Perfil 360) e eles moram no estado do componente. Sem isto,
 * filtrar "365 dias ou mais" na tela e clicar em exportar devolvia os 180+
 * inteiros — a planilha não batia com o que estava à frente dos olhos.
 *
 * `ids` usa a mesma chave de `ItemExportavel.id` do perfil daquela aba, então
 * só vale para o modelo da própria aba.
 */
export interface RecorteDaTela {
  /** Ids visíveis na tela, na ordem dela. */
  ids: string[];
  /** Frase curta com os filtros internos ativos, para o modal e os Metadados. */
  descricao: string;
  /** Quantos itens a aba teria sem os filtros internos. */
  totalSemFiltro: number;
}

/** Uma linha candidata a entrar na planilha, como o modal a mostra. */
export interface ItemExportavel<T = unknown> {
  id: string;
  titulo: string;
  subtitulo: string;
  /** Valor em R$ usado para ordenar e para somar a prévia do modal. */
  valor: number;
  dado: T;
}

export interface PerfilExportacao<T = any> {
  /** Mesmo id da aba (TABS em index.tsx). */
  id: string;
  titulo: string;
  /** O que este modelo exporta — aparece no modal e na aba Metadados. */
  descricao: string;
  /** Nome da entidade no plural: "clientes", "faturas", "unidades"... */
  unidade: string;
  /** Critério de ordenação, para o operador saber o que é "os 30 primeiros". */
  criterioOrdem: string;
  /** Prefixo do nome do arquivo (o carimbo de data/hora entra depois). */
  arquivo: string;
  /** Atalhos de "Top N" oferecidos no modal. */
  atalhosTopN: number[];
  itens: (ctx: ContextoExportacao) => ItemExportavel<T>[];
  planilhas: (itens: ItemExportavel<T>[], ctx: ContextoExportacao) => Planilha[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const somaValor = (itens: ItemExportavel[]) => itens.reduce((s, i) => s + i.valor, 0);

const dados = <T>(itens: ItemExportavel<T>[]): T[] => itens.map(i => i.dado);

const simNao = (v: boolean) => (v ? 'Sim' : 'Não');

/** 'yyyy-mm' -> 'mm/yyyy'. Devolve o que veio quando não reconhece. */
const competenciaBR = (comp: string | null | undefined) => {
  if (!comp) return '';
  const [ano, mes] = comp.split('-');
  return ano && mes ? `${mes}/${ano}` : comp;
};

/** Dias entre `hoje` e a data ISO. Vazio sem data. */
const atrasoEmDias = (iso: string | null | undefined, hoje: Date): number | '' => {
  if (!iso) return '';
  const alvo = new Date(iso);
  if (Number.isNaN(alvo.getTime())) return '';
  return Math.floor((hoje.getTime() - alvo.getTime()) / 86400000);
};

const vencimentoDaFatura = (f: FaturaInadimplente) => f.vencimento_boleto || f.data_vencimento || '';

const faixaDeAtraso = (dias: number) => {
  if (dias <= 15) return 'Até 15 dias';
  if (dias <= 30) return '16 a 30 dias';
  if (dias <= 60) return '31 a 60 dias';
  if (dias <= 90) return '61 a 90 dias';
  if (dias < 180) return '91 a 179 dias';
  if (dias < 365) return '180 a 364 dias';
  return '365 dias ou mais';
};

const externos = (ctx: ContextoExportacao) => ctx.clientes.filter(c => !c.interno);

/** dd/mm/aaaa a partir de ISO, sem passar por Date (evita −1 dia por fuso). */
const isoParaBR = (iso: string) => iso.split('T')[0].split('-').reverse().join('/');

/** Descrição legível dos filtros globais, para a aba Metadados. */
export const descreverFiltros = (filtros: FiltrosGlobais): string => {
  const partes: string[] = [];
  if (filtros.dataInicio || filtros.dataFim) {
    const rotulo = filtros.periodoModo === 'competencia' ? 'Mês da fatura' : 'Vencimento';
    partes.push(`${rotulo}: ${filtros.dataInicio ? isoParaBR(filtros.dataInicio) : 'início'} a ${filtros.dataFim ? isoParaBR(filtros.dataFim) : 'fim'}`);
  }
  if (filtros.cidade !== 'Todas') partes.push(`Cidade: ${filtros.cidade}`);
  if (filtros.risco !== 'Todos') partes.push(`Risco: ${filtros.risco}`);
  if (filtros.score !== 'Todos') partes.push(`Score: ${filtros.score}`);
  if (filtros.saldoAcumulado !== 'Todos') partes.push(filtros.saldoAcumulado);
  if (filtros.busca) partes.push(`Busca: "${filtros.busca}"`);
  return partes.length ? partes.join(' | ') : 'Sem filtros ativos';
};

/** Item do modal a partir de um cliente consolidado. */
const itemDoCliente = (c: ClienteConsolidado): ItemExportavel<ClienteConsolidado> => ({
  id: c.nome,
  titulo: c.nome,
  subtitulo: [
    `${c.qtdFaturas} fatura${c.qtdFaturas === 1 ? '' : 's'}`,
    `${c.diasAtrasoMax} dias de atraso`,
    c.cidades.join(', ') || 'sem cidade',
  ].join(' · '),
  valor: c.valorTotal,
  dado: c,
});

interface FaturaComCliente {
  fatura: FaturaInadimplente;
  cliente: ClienteConsolidado;
  atraso: number;
  valor: number;
  avisos: Set<string>;
}

/** Expande as faturas dos clientes, já com atraso, valor devido e avisos. */
const expandirFaturas = (clientes: ClienteConsolidado[], ctx: ContextoExportacao): FaturaComCliente[] =>
  clientes.flatMap(c => c.faturas.map(f => {
    const atraso = atrasoEmDias(vencimentoDaFatura(f), ctx.hoje);
    return {
      fatura: f,
      cliente: c,
      atraso: typeof atraso === 'number' ? atraso : 0,
      valor: valorDevido(f),
      avisos: ctx.avisosPorFatura.get(String(f.id)) || new Set<string>(),
    };
  }));

const itemDaFatura = (f: FaturaComCliente): ItemExportavel<FaturaComCliente> => ({
  id: String(f.fatura.id),
  titulo: `${f.cliente.nome} · UC ${f.fatura.uc_codigo || '—'}`,
  subtitulo: [
    f.fatura.mes_referencia || 'sem competência',
    `${f.atraso} dias de atraso`,
    f.fatura.status_boleto_vigor || 'sem status',
  ].join(' · '),
  valor: f.valor,
  dado: f,
});

/**
 * Uma unidade consumidora do cliente, já casada com o cadastro quando ele
 * existe. Nasce das faturas (garante cobrir UC que não entrou na lista da
 * Rota) e é enriquecida com endereço, telefone e coordenada.
 */
interface LinhaUnidade {
  ucCodigo: string;
  clienteNome: string;
  cpfCnpj: string | null;
  uc: UcInadimplente | null;
  qtdFaturas: number;
  valor: number;
  vencMaisAntigo: string | null;
  diasAtraso: number;
  cidade: string;
}

const linhaDaUc = (u: UcInadimplente): LinhaUnidade => ({
  ucCodigo: u.ucCodigo,
  clienteNome: u.clienteNome,
  cpfCnpj: u.cpfCnpj,
  uc: u,
  qtdFaturas: u.qtdFaturas,
  valor: u.valorTotal,
  vencMaisAntigo: u.vencMaisAntigo,
  diasAtraso: u.diasAtrasoMax,
  cidade: u.cidade,
});

/** As unidades dos clientes escolhidos, com o cadastro da Rota quando houver. */
const unidadesDosClientes = (clientes: ClienteConsolidado[], ctx: ContextoExportacao): LinhaUnidade[] => {
  const nomes = new Set(clientes.map(c => c.nome));
  const cadastroPorUc = new Map<string, UcInadimplente>();
  ctx.ucs.forEach(u => { if (nomes.has(u.clienteNome)) cadastroPorUc.set(`${u.clienteNome}|${u.ucCodigo}`, u); });

  const porUc = new Map<string, LinhaUnidade>();
  clientes.forEach(c => {
    c.faturas.forEach(f => {
      const codigo = f.uc_codigo || 'sem UC';
      const chave = `${c.nome}|${codigo}`;
      if (!porUc.has(chave)) {
        const cadastro = cadastroPorUc.get(chave) ?? null;
        porUc.set(chave, {
          ucCodigo: codigo,
          clienteNome: c.nome,
          cpfCnpj: c.cpfCnpj ?? cadastro?.cpfCnpj ?? null,
          uc: cadastro,
          qtdFaturas: 0,
          valor: 0,
          vencMaisAntigo: null,
          diasAtraso: 0,
          cidade: cadastro?.cidade || f.end_cidade || '',
        });
      }
      const linha = porUc.get(chave)!;
      linha.qtdFaturas += 1;
      linha.valor += valorDevido(f);
      const venc = vencimentoDaFatura(f);
      if (venc && (!linha.vencMaisAntigo || venc < linha.vencMaisAntigo)) linha.vencMaisAntigo = venc;
    });
  });

  return Array.from(porUc.values())
    .map(l => {
      const atraso = atrasoEmDias(l.vencMaisAntigo, ctx.hoje);
      return { ...l, diasAtraso: typeof atraso === 'number' ? Math.max(0, atraso) : 0 };
    })
    .sort((a, b) => b.valor - a.valor);
};

/** Clientes consolidados que estão por trás de um conjunto de faturas. */
const clientesDasFaturas = (faturas: FaturaComCliente[]): ClienteConsolidado[] => {
  const vistos = new Map<string, ClienteConsolidado>();
  faturas.forEach(f => { if (!vistos.has(f.cliente.nome)) vistos.set(f.cliente.nome, f.cliente); });
  return Array.from(vistos.values());
};

/** Clientes consolidados por trás de um conjunto de unidades. */
const clientesDasUnidades = (linhas: LinhaUnidade[], ctx: ContextoExportacao): ClienteConsolidado[] => {
  const nomes = new Set(linhas.map(l => l.clienteNome));
  return ctx.clientes.filter(c => nomes.has(c.nome));
};

// ---------------------------------------------------------------------------
// Colunas
// ---------------------------------------------------------------------------

const COLUNAS_CLIENTE: Coluna<ClienteConsolidado>[] = [
  { header: 'Cliente', tipo: 'txt', largura: 36, valor: c => c.nome },
  { header: 'CPF/CNPJ', tipo: 'txt', largura: 20, valor: c => c.cpfCnpj || '' },
  { header: 'Tipo', tipo: 'txt', largura: 10, valor: c => (c.interno ? 'Interno' : 'Externo') },
  { header: 'UCs', tipo: 'txt', largura: 24, valor: c => c.ucs.join(', ') },
  { header: 'Qtd. UCs', tipo: 'num', largura: 9, valor: c => c.ucs.length },
  { header: 'Faturas', tipo: 'num', largura: 9, valor: c => c.qtdFaturas },
  { header: 'Valor em aberto', tipo: 'brl', largura: 16, valor: c => c.valorTotal },
  { header: 'Saldo retido (kWh)', tipo: 'kwh', largura: 16, valor: c => c.saldoAcumulado },
  { header: 'Venc. mais antigo', tipo: 'data', largura: 14, valor: c => dataParaSerialExcel(c.vencMaisAntigo) },
  { header: 'Dias em atraso', tipo: 'num', largura: 12, valor: c => c.diasAtrasoMax },
  { header: 'Idade da dívida (dias)', tipo: 'num', largura: 14, valor: c => c.diasDividaMax },
  { header: 'Competência mais antiga', tipo: 'txt', largura: 16, valor: c => competenciaBR(c.competenciaMaisAntiga) },
  { header: 'Competências em aberto', tipo: 'txt', largura: 30, valor: c => c.competencias.map(competenciaBR).join(', ') },
  { header: 'Situação da UC', tipo: 'txt', largura: 20, valor: c => (c.rateioFinalizado ? 'Rateio encerrado' : c.situacaoRateio === 'sem_rateio' ? 'Sem rateio' : (c.situacoesUc.join(', ') || 'Não informada')) },
  { header: 'Rateio encerrado', tipo: 'txt', largura: 14, valor: c => simNao(c.rateioFinalizado) },
  { header: 'Risco', tipo: 'txt', largura: 22, valor: c => c.risco },
  { header: 'Score', tipo: 'txt', largura: 7, valor: c => c.score },
  { header: 'Curva ABC', tipo: 'txt', largura: 10, valor: c => c.curvaAbc },
  { header: '% da carteira', tipo: 'pct', largura: 12, valor: c => c.pct },
  { header: '% acumulado', tipo: 'pct', largura: 12, valor: c => c.pctAcum },
  { header: 'Cidades', tipo: 'txt', largura: 26, valor: c => c.cidades.join(', ') },
  { header: 'Consultor', tipo: 'txt', largura: 22, valor: c => c.consultor_responsavel || '' },
  { header: 'Titularidade', tipo: 'txt', largura: 18, valor: c => c.titularidade || '' },
];

/**
 * Tudo que existe na fatura. É de propósito que seja larga: no Excel o
 * operador esconde coluna, mas não inventa dado que a planilha não trouxe — e
 * a exportação antiga obrigava a voltar no sistema para ver linha digitável,
 * PIX, endereço do pagador ou o motivo de um cancelamento.
 */
const COLUNAS_FATURA: Coluna<FaturaComCliente>[] = [
  { header: 'Cliente', tipo: 'txt', largura: 36, valor: f => f.cliente.nome },
  { header: 'CPF/CNPJ', tipo: 'txt', largura: 20, valor: f => f.cliente.cpfCnpj || f.fatura.cliente_cpf_cnpj || '' },
  { header: 'UC', tipo: 'txt', largura: 16, valor: f => f.fatura.uc_codigo || '' },
  { header: 'Competência', tipo: 'txt', largura: 12, valor: f => f.fatura.mes_referencia || '' },
  { header: 'Valor cobrado', tipo: 'brl', largura: 16, valor: f => f.valor },
  { header: 'Valor boleto Vigor', tipo: 'brl', largura: 16, valor: f => Number(f.fatura.valor_boleto_vigor) || 0 },
  { header: 'Valor boleto único', tipo: 'brl', largura: 16, valor: f => Number(f.fatura.valor_emissao_boleto_unico) || 0 },
  { header: 'Valor em parcelamentos', tipo: 'brl', largura: 18, valor: f => Number(f.fatura.valor_parcelamentos_boleto) || 0 },
  { header: 'Vencimento do boleto', tipo: 'data', largura: 16, valor: f => dataParaSerialExcel(f.fatura.vencimento_boleto) },
  { header: 'Vencimento da fatura', tipo: 'data', largura: 16, valor: f => dataParaSerialExcel(f.fatura.data_vencimento) },
  { header: 'Data de pagamento', tipo: 'data', largura: 15, valor: f => dataParaSerialExcel(f.fatura.data_pgto_boleto) },
  { header: 'Dias em atraso', tipo: 'num', largura: 12, valor: f => f.atraso },
  { header: 'Faixa de atraso', tipo: 'txt', largura: 16, valor: f => faixaDeAtraso(f.atraso) },
  { header: 'Status do boleto', tipo: 'txt', largura: 18, valor: f => f.fatura.status_boleto_vigor || '' },
  { header: 'Status da fatura', tipo: 'txt', largura: 16, valor: f => f.fatura.status_fatura || '' },
  { header: 'Parcelamento', tipo: 'txt', largura: 22, valor: f => f.fatura.parcelamento_descricao || '' },
  { header: 'Motivo do cancelamento', tipo: 'txt', largura: 26, valor: f => f.fatura.motivo_cancelamento_boleto || '' },
  { header: 'Saldo retido (kWh)', tipo: 'kwh', largura: 16, valor: f => Number(f.fatura.saldo_acumulado_kwh) || 0 },
  { header: 'Nº da cobrança', tipo: 'txt', largura: 16, valor: f => f.fatura.num_cobranca_boleto || '' },
  { header: 'Linha digitável', tipo: 'txt', largura: 52, valor: f => f.fatura.linha_digitavel_boleto || '' },
  { header: 'Código PIX', tipo: 'txt', largura: 40, valor: f => f.fatura.codigo_pix_boleto || '' },
  { header: 'Link do boleto', tipo: 'txt', largura: 40, valor: f => f.fatura.url_boleto_combinado || f.fatura.url_boleto || '' },
  { header: 'PDF do boleto', tipo: 'txt', largura: 40, valor: f => f.fatura.pdf_boleto_combinado || '' },
  { header: 'PDF da fatura', tipo: 'txt', largura: 40, valor: f => f.fatura.pdf_fatura || '' },
  { header: 'Logradouro', tipo: 'txt', largura: 34, valor: f => f.fatura.end_logradouro || '' },
  { header: 'Número', tipo: 'txt', largura: 10, valor: f => f.fatura.end_numero || '' },
  { header: 'Bairro', tipo: 'txt', largura: 22, valor: f => f.fatura.end_bairro || '' },
  { header: 'Cidade', tipo: 'txt', largura: 22, valor: f => f.fatura.end_cidade || '' },
  { header: 'CEP', tipo: 'txt', largura: 12, valor: f => f.fatura.end_cep || '' },
  { header: 'UF', tipo: 'txt', largura: 6, valor: f => f.fatura.end_uf || '' },
  { header: 'Situação da UC', tipo: 'txt', largura: 16, valor: f => f.fatura.situacao_uc || '' },
  { header: 'Consultor', tipo: 'txt', largura: 22, valor: f => f.fatura.consultor_responsavel || f.cliente.consultor_responsavel || '' },
  { header: 'Titularidade', tipo: 'txt', largura: 18, valor: f => f.fatura.titularidade || f.cliente.titularidade || '' },
  { header: 'Risco do cliente', tipo: 'txt', largura: 22, valor: f => f.cliente.risco },
  { header: 'Score', tipo: 'txt', largura: 7, valor: f => f.cliente.score },
];

/** Colunas de fatura + a régua automática, para a aba "Vencidas 15+ dias". */
const COLUNAS_FATURA_COM_AVISOS: Coluna<FaturaComCliente>[] = [
  ...COLUNAS_FATURA,
  ...TIPOS_AVISO.map((tipo): Coluna<FaturaComCliente> => ({
    header: tipo === 'Antes' ? 'Aviso antes do vencimento' : `Aviso ${tipo}`,
    tipo: 'txt',
    largura: 11,
    valor: f => simNao(avisoEnviado(f.avisos, tipo)),
  })),
  { header: 'Régua completa', tipo: 'txt', largura: 13, valor: f => simNao(recebeuTodosOsAvisos(f.avisos)) },
];

/** Tudo que se sabe da unidade: endereço, contato, cadastro e coordenada. */
const COLUNAS_UNIDADE: Coluna<LinhaUnidade>[] = [
  { header: 'UC', tipo: 'txt', largura: 16, valor: l => l.ucCodigo },
  { header: 'Cliente', tipo: 'txt', largura: 32, valor: l => l.clienteNome },
  { header: 'CPF/CNPJ', tipo: 'txt', largura: 20, valor: l => l.cpfCnpj || '' },
  { header: 'Titular na Equatorial', tipo: 'txt', largura: 32, valor: l => l.uc?.titular || '' },
  { header: 'Endereço', tipo: 'txt', largura: 52, valor: l => l.uc?.enderecoTexto || '' },
  { header: 'Logradouro', tipo: 'txt', largura: 34, valor: l => l.uc?.cadastro?.logradouro || '' },
  { header: 'Número', tipo: 'txt', largura: 10, valor: l => l.uc?.cadastro?.numero || '' },
  { header: 'Complemento', tipo: 'txt', largura: 18, valor: l => l.uc?.cadastro?.complemento || '' },
  { header: 'Bairro', tipo: 'txt', largura: 22, valor: l => l.uc?.bairro || '' },
  { header: 'Cidade', tipo: 'txt', largura: 22, valor: l => l.cidade },
  { header: 'CEP', tipo: 'txt', largura: 12, valor: l => l.uc?.cadastro?.cep || '' },
  { header: 'UF', tipo: 'txt', largura: 6, valor: l => l.uc?.cadastro?.estado || '' },
  { header: 'Telefone 1', tipo: 'txt', largura: 16, valor: l => l.uc?.telefones[0] || '' },
  { header: 'Telefone 2', tipo: 'txt', largura: 16, valor: l => l.uc?.telefones[1] || '' },
  { header: 'WhatsApp do cadastro', tipo: 'txt', largura: 18, valor: l => l.uc?.cadastro?.whatsapp_cliente || '' },
  { header: 'Faturas na UC', tipo: 'num', largura: 11, valor: l => l.qtdFaturas },
  { header: 'Valor em aberto', tipo: 'brl', largura: 16, valor: l => l.valor },
  { header: 'Venc. mais antigo', tipo: 'data', largura: 14, valor: l => dataParaSerialExcel(l.vencMaisAntigo) },
  { header: 'Dias em atraso', tipo: 'num', largura: 12, valor: l => l.diasAtraso },
  { header: 'Situação da UC', tipo: 'txt', largura: 16, valor: l => l.uc?.situacaoUc || '' },
  { header: 'Endereço confirmado', tipo: 'txt', largura: 18, valor: l => simNao(!!l.uc?.coordenadaConfiavel) },
  { header: 'Geocode', tipo: 'txt', largura: 14, valor: l => l.uc?.geocodeStatus || '' },
  { header: 'Endereço do mapa', tipo: 'txt', largura: 44, valor: l => l.uc?.cadastro?.geocode_label || '' },
  { header: 'Latitude', tipo: 'txt', largura: 14, valor: l => (l.uc?.latitude === null || l.uc?.latitude === undefined ? '' : String(l.uc.latitude)) },
  { header: 'Longitude', tipo: 'txt', largura: 14, valor: l => (l.uc?.longitude === null || l.uc?.longitude === undefined ? '' : String(l.uc.longitude)) },
  { header: 'PDF da fatura', tipo: 'txt', largura: 40, valor: l => l.uc?.pdfFatura || '' },
];

// ---------------------------------------------------------------------------
// Abas
// ---------------------------------------------------------------------------

const abaClientes = (clientes: ClienteConsolidado[], nome = 'Clientes'): Planilha => montarPlanilha(
  nome,
  COLUNAS_CLIENTE,
  clientes,
  linhaDeTotais(COLUNAS_CLIENTE, `TOTAL (${clientes.length} clientes)`, {
    'Faturas': clientes.reduce((s, c) => s + c.qtdFaturas, 0),
    'Valor em aberto': clientes.reduce((s, c) => s + c.valorTotal, 0),
    'Saldo retido (kWh)': clientes.reduce((s, c) => s + c.saldoAcumulado, 0),
  }),
);

const abaFaturas = (
  faturas: FaturaComCliente[],
  nome = 'Faturas',
  colunas: Coluna<FaturaComCliente>[] = COLUNAS_FATURA,
): Planilha => montarPlanilha(
  nome,
  colunas,
  faturas,
  linhaDeTotais(colunas, `TOTAL (${faturas.length} faturas)`, {
    'Valor cobrado': faturas.reduce((s, f) => s + f.valor, 0),
  }),
);

const abaUnidades = (linhas: LinhaUnidade[], nome = 'Unidades consumidoras'): Planilha => montarPlanilha(
  nome,
  COLUNAS_UNIDADE,
  linhas,
  linhaDeTotais(COLUNAS_UNIDADE, `TOTAL (${linhas.length} unidades)`, {
    'Faturas na UC': linhas.reduce((s, l) => s + l.qtdFaturas, 0),
    'Valor em aberto': linhas.reduce((s, l) => s + l.valor, 0),
  }),
);

/**
 * As abas de detalhe que acompanham TODO modelo.
 *
 * Exportar os 30 maiores devedores e receber só nome e total não resolve: a
 * pergunta seguinte é sempre "quais faturas são essas?" e "onde fica a UC?".
 * Por isso a fatura a fatura e a unidade a unidade dos escolhidos vão junto,
 * sempre — o recorte é de quem entra, não do que se sabe sobre eles.
 */
const abasDeDetalhe = (
  clientes: ClienteConsolidado[],
  ctx: ContextoExportacao,
  opcoes: { comClientes?: boolean; comFaturas?: boolean; comUnidades?: boolean } = {},
): Planilha[] => {
  const { comClientes = true, comFaturas = true, comUnidades = true } = opcoes;
  const abas: Planilha[] = [];
  if (comClientes) abas.push(abaClientes(clientes));
  if (comFaturas) abas.push(abaFaturas(expandirFaturas(clientes, ctx)));
  if (comUnidades) abas.push(abaUnidades(unidadesDosClientes(clientes, ctx)));
  return abas;
};

/** Indicadores da carteira — os mesmos números dos cartões da Visão Executiva. */
const abaIndicadores = (ctx: ContextoExportacao): Planilha => {
  const ext = externos(ctx);
  const int = ctx.clientes.filter(c => c.interno);
  const top10 = ctx.clientes.slice(0, 10).reduce((s, c) => s + c.valorTotal, 0);
  return {
    nome: 'Indicadores',
    headers: ['Indicador', 'Valor'],
    rows: [
      ['Exposição total (R$)', ctx.totalAberto],
      ['Faturas em aberto', ctx.clientes.reduce((s, c) => s + c.qtdFaturas, 0)],
      ['Clientes inadimplentes', ctx.clientes.length],
      ['Clientes externos', ext.length],
      ['Clientes internos', int.length],
      ['Valor dos externos (R$)', ext.reduce((s, c) => s + c.valorTotal, 0)],
      ['Valor dos internos (R$)', int.reduce((s, c) => s + c.valorTotal, 0)],
      ['Saldo retido total (kWh)', ctx.clientes.reduce((s, c) => s + c.saldoAcumulado, 0)],
      ['Concentração no Top 10 (R$)', top10],
      ['Concentração no Top 10 (%)', ctx.totalAberto > 0 ? (top10 / ctx.totalAberto) * 100 : 0],
    ],
    colTypes: ['txt', 'num'],
    widths: [34, 20],
  };
};

/**
 * Aba Metadados: o que foi exportado, com qual recorte e sob quais filtros.
 * Anexada a TODO arquivo por `montarExportacao` — sem ela ninguém sabe, três
 * semanas depois, se a planilha é a carteira inteira ou só os 30 primeiros.
 */
export const abaMetadados = (
  perfil: PerfilExportacao,
  itens: ItemExportavel[],
  ctx: ContextoExportacao,
  recorte: string,
): Planilha => ({
  nome: 'Metadados',
  headers: ['Parâmetro', 'Valor'],
  rows: [
    ['Gerado em', ctx.hoje.toLocaleString('pt-BR')],
    ['Visão exportada', perfil.titulo],
    ['O que esta visão traz', perfil.descricao],
    ['Recorte escolhido', recorte],
    ['Ordenação', perfil.criterioOrdem],
    ['Filtros globais aplicados', descreverFiltros(ctx.filtros)],
    [`Total de ${perfil.unidade} exportados`, itens.length],
    ['Valor somado (R$)', somaValor(itens)],
  ],
  colTypes: ['txt', 'txt'],
  widths: [30, 70],
});

// ---------------------------------------------------------------------------
// Perfis, um por aba
// ---------------------------------------------------------------------------

const perfilVisaoExecutiva: PerfilExportacao<ClienteConsolidado> = {
  id: 'visao',
  titulo: 'Visão Executiva (carteira completa)',
  descricao: 'Carteira consolidada por cliente, com curva ABC, risco e score, mais as faturas linha a linha, as unidades e os indicadores da tela.',
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor valor em aberto',
  arquivo: 'Inadimplencia_Carteira',
  atalhosTopN: [10, 30, 50, 100],
  itens: ctx => ctx.clientes.map(itemDoCliente),
  planilhas: (itens, ctx) => [...abasDeDetalhe(dados(itens), ctx), abaIndicadores(ctx)],
};

const perfilRecebimentos: PerfilExportacao<FaturaComCliente> = {
  id: 'recebimentos',
  titulo: 'Recebimentos (fatura a fatura)',
  descricao: 'Uma linha por fatura em aberto, com vencimento, atraso, meios de pagamento e status do boleto — mais os clientes e as unidades por trás delas.',
  unidade: 'faturas',
  criterioOrdem: 'da fatura mais atrasada para a menos atrasada',
  arquivo: 'Inadimplencia_Recebimentos',
  atalhosTopN: [30, 50, 100, 300],
  itens: ctx => expandirFaturas(ctx.clientes, ctx).sort((a, b) => b.atraso - a.atraso).map(itemDaFatura),
  planilhas: (itens, ctx) => {
    const faturas = dados(itens);
    const clientes = clientesDasFaturas(faturas);
    return [
      abaFaturas(faturas),
      abaClientes(clientes),
      abaUnidades(unidadesDosClientes(clientes, ctx)),
    ];
  },
};

const perfilPerfil360: PerfilExportacao<ClienteConsolidado> = {
  id: 'perfil',
  titulo: 'Perfil 360 do cliente',
  descricao: 'Ficha completa dos clientes escolhidos: dados consolidados, todas as faturas em aberto e todas as unidades com endereço e contato.',
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor valor em aberto',
  arquivo: 'Inadimplencia_Perfil360',
  atalhosTopN: [1, 5, 10, 30],
  itens: ctx => ctx.clientes.map(itemDoCliente),
  planilhas: (itens, ctx) => abasDeDetalhe(dados(itens), ctx),
};

const QUADRANTES = [
  { id: 'Q1', rotulo: 'Risco estratégico (alto débito + alto saldo)' },
  { id: 'Q2', rotulo: 'Risco alto (alto débito, sem saldo)' },
  { id: 'Q3', rotulo: 'Risco médio (baixo débito, com saldo)' },
  { id: 'Q4', rotulo: 'Risco baixo (baixo débito, sem saldo)' },
];

/** Mesmo corte da tela: R$ 5.000 em débito e 5.000 kWh de saldo. */
const quadranteDoCliente = (c: ClienteConsolidado) => {
  const altoValor = c.valorTotal > 5000;
  const altoSaldo = c.saldoAcumulado > 5000;
  if (altoValor && altoSaldo) return QUADRANTES[0];
  if (altoValor) return QUADRANTES[1];
  if (altoSaldo) return QUADRANTES[2];
  return QUADRANTES[3];
};

const perfilMatrizRisco: PerfilExportacao<ClienteConsolidado> = {
  id: 'matriz',
  titulo: 'Matriz de Risco',
  descricao: 'Clientes externos classificados nos quatro quadrantes de valor em aberto x saldo retido, com o resumo de cada quadrante, as faturas e as unidades.',
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor valor em aberto',
  arquivo: 'Inadimplencia_MatrizRisco',
  atalhosTopN: [10, 30, 50],
  itens: ctx => externos(ctx).map(itemDoCliente),
  planilhas: (itens, ctx) => {
    const clientes = dados(itens);
    const colunas: Coluna<ClienteConsolidado>[] = [
      { header: 'Quadrante', tipo: 'txt', largura: 10, valor: c => quadranteDoCliente(c).id },
      { header: 'Perfil de risco', tipo: 'txt', largura: 40, valor: c => quadranteDoCliente(c).rotulo },
      ...COLUNAS_CLIENTE,
    ];
    const noQuadrante = (q: string) => clientes.filter(c => quadranteDoCliente(c).id === q);
    const colunasResumo: Coluna<typeof QUADRANTES[number]>[] = [
      { header: 'Quadrante', tipo: 'txt', largura: 10, valor: q => q.id },
      { header: 'Perfil de risco', tipo: 'txt', largura: 42, valor: q => q.rotulo },
      { header: 'Clientes', tipo: 'num', largura: 10, valor: q => noQuadrante(q.id).length },
      { header: 'Valor em aberto', tipo: 'brl', largura: 16, valor: q => noQuadrante(q.id).reduce((s, c) => s + c.valorTotal, 0) },
      { header: 'Saldo retido (kWh)', tipo: 'kwh', largura: 16, valor: q => noQuadrante(q.id).reduce((s, c) => s + c.saldoAcumulado, 0) },
    ];
    return [
      montarPlanilha('Clientes por quadrante', colunas, clientes, linhaDeTotais(colunas, `TOTAL (${clientes.length} clientes)`, {
        'Valor em aberto': clientes.reduce((s, c) => s + c.valorTotal, 0),
        'Saldo retido (kWh)': clientes.reduce((s, c) => s + c.saldoAcumulado, 0),
      })),
      montarPlanilha('Resumo por quadrante', colunasResumo, QUADRANTES),
      ...abasDeDetalhe(clientes, ctx, { comClientes: false }),
    ];
  },
};

const perfilRanking: PerfilExportacao<ClienteConsolidado> = {
  id: 'ranking',
  titulo: 'Maiores Devedores',
  descricao: 'Ranking dos clientes externos por valor em aberto, numerado como na tela, seguido das faturas e das unidades de cada um.',
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor valor em aberto',
  arquivo: 'Inadimplencia_MaioresDevedores',
  atalhosTopN: [10, 20, 30, 50],
  itens: ctx => externos(ctx).map(itemDoCliente),
  planilhas: (itens, ctx) => {
    const clientes = dados(itens);
    const colunas: Coluna<ClienteConsolidado>[] = [
      { header: '#', tipo: 'num', largura: 6, valor: (_c, i) => i + 1 },
      ...COLUNAS_CLIENTE,
    ];
    return [
      montarPlanilha('Ranking', colunas, clientes, linhaDeTotais(colunas, `TOTAL (${clientes.length} clientes)`, {
        'Valor em aberto': clientes.reduce((s, c) => s + c.valorTotal, 0),
        'Faturas': clientes.reduce((s, c) => s + c.qtdFaturas, 0),
        'Saldo retido (kWh)': clientes.reduce((s, c) => s + c.saldoAcumulado, 0),
      })),
      ...abasDeDetalhe(clientes, ctx, { comClientes: false }),
    ];
  },
};

const perfilSaldos: PerfilExportacao<ClienteConsolidado> = {
  id: 'saldos',
  titulo: 'Saldos Acumulados',
  descricao: 'Só quem tem energia retida: saldo em kWh bloqueado sob dívida, do maior para o menor, com as faturas e as unidades por trás do saldo.',
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor saldo retido',
  arquivo: 'Inadimplencia_SaldosAcumulados',
  atalhosTopN: [10, 30, 50],
  itens: ctx => ctx.clientes
    .filter(c => c.saldoAcumulado > 0)
    .slice()
    .sort((a, b) => b.saldoAcumulado - a.saldoAcumulado)
    .map(itemDoCliente),
  planilhas: (itens, ctx) => {
    const clientes = dados(itens);
    return [abaClientes(clientes, 'Saldos retidos'), ...abasDeDetalhe(clientes, ctx, { comClientes: false })];
  },
};

interface LinhaCidade {
  cidade: string;
  clientes: number;
  valor: number;
  faturas: number;
}

/** Mesma conta do mapa: cliente com N cidades rateia o valor entre elas. */
const agruparPorCidade = (clientes: ClienteConsolidado[]): LinhaCidade[] => {
  const mapa = new Map<string, LinhaCidade>();
  clientes.forEach(c => {
    const cidades = c.cidades.length ? c.cidades : ['Sem cidade'];
    cidades.forEach(cid => {
      const atual = mapa.get(cid) || { cidade: cid, clientes: 0, valor: 0, faturas: 0 };
      atual.clientes += 1;
      atual.valor += c.valorTotal / cidades.length;
      atual.faturas += c.qtdFaturas;
      mapa.set(cid, atual);
    });
  });
  return Array.from(mapa.values()).sort((a, b) => b.valor - a.valor);
};

const perfilMapa: PerfilExportacao<LinhaCidade> = {
  id: 'mapa',
  titulo: 'Mapa de Goiás (por cidade)',
  descricao: 'Inadimplência distribuída por cidade — cliente com UC em mais de uma cidade tem o valor rateado, igual ao heatmap — com os clientes, faturas e unidades das cidades escolhidas.',
  unidade: 'cidades',
  criterioOrdem: 'da cidade com maior valor em aberto para a menor',
  arquivo: 'Inadimplencia_PorCidade',
  atalhosTopN: [5, 10, 20],
  itens: ctx => agruparPorCidade(ctx.clientes).map(l => ({
    id: l.cidade,
    titulo: l.cidade,
    subtitulo: `${l.clientes} clientes · ${l.faturas} faturas`,
    valor: l.valor,
    dado: l,
  })),
  planilhas: (itens, ctx) => {
    const linhas = dados(itens);
    const cidades = new Set(linhas.map(l => l.cidade));
    const clientes = ctx.clientes.filter(c => (c.cidades.length ? c.cidades : ['Sem cidade']).some(cid => cidades.has(cid)));
    const colunas: Coluna<LinhaCidade>[] = [
      { header: 'Cidade', tipo: 'txt', largura: 28, valor: l => l.cidade },
      { header: 'Clientes', tipo: 'num', largura: 10, valor: l => l.clientes },
      { header: 'Faturas', tipo: 'num', largura: 10, valor: l => l.faturas },
      { header: 'Valor em aberto', tipo: 'brl', largura: 18, valor: l => l.valor },
      { header: '% da carteira', tipo: 'pct', largura: 14, valor: l => (ctx.totalAberto > 0 ? (l.valor / ctx.totalAberto) * 100 : 0) },
    ];
    return [
      montarPlanilha('Cidades', colunas, linhas, linhaDeTotais(colunas, `TOTAL (${linhas.length} cidades)`, {
        'Clientes': linhas.reduce((s, l) => s + l.clientes, 0),
        'Faturas': linhas.reduce((s, l) => s + l.faturas, 0),
        'Valor em aberto': linhas.reduce((s, l) => s + l.valor, 0),
      })),
      ...abasDeDetalhe(clientes, ctx),
    ];
  },
};

const perfilInsights: PerfilExportacao<InsightCarteira> = {
  id: 'insights',
  titulo: 'Insights da carteira',
  descricao: 'A leitura automática da carteira — diagnóstico e ação sugerida de cada ponto — com os indicadores que a sustentam e a carteira inteira por trás.',
  unidade: 'insights',
  criterioOrdem: 'na ordem em que aparecem na tela',
  arquivo: 'Inadimplencia_Insights',
  atalhosTopN: [],
  itens: ctx => calcularInsightsCarteira(ctx.clientes, ctx.totalAberto).map(i => ({
    id: i.id,
    titulo: i.titulo,
    subtitulo: i.nivel,
    valor: 0,
    dado: i,
  })),
  planilhas: (itens, ctx) => {
    const colunas: Coluna<InsightCarteira>[] = [
      { header: 'Insight', tipo: 'txt', largura: 40, valor: i => i.titulo },
      { header: 'Nível', tipo: 'txt', largura: 14, valor: i => i.nivel },
      { header: 'Diagnóstico', tipo: 'txt', largura: 80, valor: i => i.texto },
      { header: 'Ação sugerida', tipo: 'txt', largura: 80, valor: i => i.recomendacao },
    ];
    return [
      montarPlanilha('Insights', colunas, dados(itens)),
      abaIndicadores(ctx),
      ...abasDeDetalhe(ctx.clientes, ctx),
    ];
  },
};

/** Piso da aba Vencidas 15+ dias. */
const DIAS_VENCIDAS = 15;

const perfilVencidas15: PerfilExportacao<FaturaComCliente> = {
  id: 'vencidas15',
  titulo: 'Vencidas 15+ dias',
  descricao: `Faturas vencidas há ${DIAS_VENCIDAS} dias ou mais, com as colunas da régua automática (antes do vencimento, D+3, D+7) para ver quem ficou sem aviso, mais os clientes e as unidades.`,
  unidade: 'faturas',
  criterioOrdem: 'da fatura mais atrasada para a menos atrasada',
  arquivo: 'Inadimplencia_Vencidas15dias',
  atalhosTopN: [30, 50, 100, 300],
  itens: ctx => expandirFaturas(ctx.clientes, ctx)
    .filter(f => f.atraso >= DIAS_VENCIDAS)
    .sort((a, b) => b.atraso - a.atraso)
    .map(itemDaFatura),
  planilhas: (itens, ctx) => {
    const faturas = dados(itens);
    const clientes = clientesDasFaturas(faturas);
    return [
      abaFaturas(faturas, 'Vencidas 15+ dias', COLUNAS_FATURA_COM_AVISOS),
      abaClientes(clientes),
      abaUnidades(unidadesDosClientes(clientes, ctx)),
    ];
  },
};

/** Corte de "débito antigo" — a régua automática termina em D+24. */
const DIAS_ANTIGOS = 180;

const perfilDebitosAntigos: PerfilExportacao<ClienteConsolidado> = {
  id: 'antigos',
  titulo: 'Débitos antigos (180+ dias)',
  descricao: `Clientes cuja dívida mais antiga passa de ${DIAS_ANTIGOS} dias, contada pela competência da fatura e não pela data do boleto. Inclui quem já teve o rateio encerrado, com faturas e unidades.`,
  unidade: 'clientes',
  criterioOrdem: 'do maior para o menor valor em aberto',
  arquivo: 'Inadimplencia_DebitosAntigos',
  atalhosTopN: [10, 30, 50],
  itens: ctx => ctx.clientes
    .filter(c => c.diasDividaMax >= DIAS_ANTIGOS)
    .slice()
    .sort((a, b) => b.valorTotal - a.valorTotal)
    .map(itemDoCliente),
  planilhas: (itens, ctx) => {
    const clientes = dados(itens);
    const colunas: Coluna<ClienteConsolidado>[] = [
      { header: 'Faixa', tipo: 'txt', largura: 16, valor: c => faixaDeAtraso(c.diasDividaMax) },
      ...COLUNAS_CLIENTE,
    ];
    return [
      montarPlanilha('Débitos antigos', colunas, clientes, linhaDeTotais(colunas, `TOTAL (${clientes.length} clientes)`, {
        'Valor em aberto': clientes.reduce((s, c) => s + c.valorTotal, 0),
        'Faturas': clientes.reduce((s, c) => s + c.qtdFaturas, 0),
      })),
      ...abasDeDetalhe(clientes, ctx, { comClientes: false }),
    ];
  },
};

const perfilRota: PerfilExportacao<UcInadimplente> = {
  id: 'rota',
  titulo: 'Visita presencial (por unidade)',
  descricao: 'Uma linha por unidade consumidora, com endereço, contato e coordenada — a lista que vai para a visita em campo, com as faturas de cada parada. Este mesmo arquivo volta pelo botão "Importar planilha e marcar" da tela.',
  unidade: 'unidades',
  criterioOrdem: 'da unidade com maior valor em aberto para a menor',
  arquivo: 'Inadimplencia_RotaCobranca',
  atalhosTopN: [10, 20, 30, 50],
  itens: ctx => ctx.ucs.map(u => ({
    id: u.chave,
    titulo: `${u.ucCodigo} · ${u.clienteNome}`,
    subtitulo: [
      u.cidade || 'sem cidade',
      `${u.qtdFaturas} fatura${u.qtdFaturas === 1 ? '' : 's'}`,
      `${u.diasAtrasoMax} dias de atraso`,
    ].join(' · '),
    valor: u.valorTotal,
    dado: u,
  })),
  planilhas: (itens, ctx) => {
    const ucs = dados(itens);
    const linhas = ucs.map(linhaDaUc);
    const clientes = clientesDasUnidades(linhas, ctx);
    // As faturas aqui são as DA PARADA, não as do cliente inteiro: o cobrador
    // vai a um endereço, e o cliente pode ter UC em outra cidade.
    const faturasDasParadas = ucs.flatMap(u => {
      const cliente = ctx.clientes.find(c => c.nome === u.clienteNome);
      return u.faturas.map(f => {
        const atraso = atrasoEmDias(vencimentoDaFatura(f), ctx.hoje);
        return {
          fatura: f,
          cliente: cliente ?? ({
            nome: u.clienteNome, cpfCnpj: u.cpfCnpj, risco: '', score: '',
            consultor_responsavel: '', titularidade: '',
          } as unknown as ClienteConsolidado),
          atraso: typeof atraso === 'number' ? atraso : 0,
          valor: valorDevido(f),
          avisos: ctx.avisosPorFatura.get(String(f.id)) || new Set<string>(),
        };
      });
    });
    return [
      abaUnidades(linhas, 'Paradas'),
      abaFaturas(faturasDasParadas),
      abaClientes(clientes),
    ];
  },
};

export const PERFIS_EXPORTACAO: PerfilExportacao[] = [
  perfilVisaoExecutiva,
  perfilRecebimentos,
  perfilPerfil360,
  perfilMatrizRisco,
  perfilRanking,
  perfilSaldos,
  perfilMapa,
  perfilInsights,
  perfilVencidas15,
  perfilDebitosAntigos,
  perfilRota,
];

/**
 * Os itens que o recorte da tela aponta, na ORDEM da tela — quem filtrou e
 * ordenou lá espera a planilha na mesma sequência. Id que não existe mais no
 * modelo (a carteira mudou embaixo, ou o recorte é de outra aba) é ignorado em
 * vez de virar linha vazia.
 */
export function itensDoRecorte<T>(
  itens: ItemExportavel<T>[],
  recorte: RecorteDaTela | null | undefined,
): ItemExportavel<T>[] {
  if (!recorte) return [];
  const porId = new Map(itens.map(i => [i.id, i]));
  return recorte.ids
    .map(id => porId.get(id))
    .filter((i): i is ItemExportavel<T> => !!i);
}

/** Perfil da aba aberta; cai na carteira completa se a aba não tiver um. */
export const perfilDaAba = (abaId: string): PerfilExportacao =>
  PERFIS_EXPORTACAO.find(p => p.id === abaId) || perfilVisaoExecutiva;

/**
 * Monta o arquivo final: as abas do perfil mais a aba Metadados.
 * `recorte` é a frase que descreve a escolha ("Os 30 primeiros", "12 escolhidos
 * a dedo"...) e vem montada do modal.
 */
export function montarExportacao(
  perfil: PerfilExportacao,
  itens: ItemExportavel[],
  ctx: ContextoExportacao,
  recorte: string,
): Planilha[] {
  return [...perfil.planilhas(itens, ctx), abaMetadados(perfil, itens, ctx, recorte)];
}
